import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { awardApi, Award, UpdateAwardDto } from 'apis/award';
import Table, { Column } from 'ui/Table';
import FilterCard, { FilterCardGroup } from 'ui/FilterCard';
import Loading from 'ui/Loading';
import Section from 'ui/Section';
import { Switch } from 'ui/shadcn';
import { toast } from 'react-toastify';

type Filter = 'ALL' | 'VISIBLE' | 'HIDDEN';

const AwardVisibilityManager: React.FC = () => {
    const navigate = useNavigate();
    const [list, setList] = useState<Award[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<Filter>('ALL');

    useEffect(() => { fetchList(); }, []);

    const fetchList = async () => {
        try {
            const res = await awardApi.getAwards({ limit: 1000 });
            setList(res.data.data);
        } catch {
            toast.error('상훈을 불러오지 못했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = async (row: Award) => {
        try {
            const next = !row.is_visible;
            await awardApi.updateAward(row.id, { is_visible: next } as UpdateAwardDto);
            setList(prev => prev.map(a => a.id === row.id ? { ...a, is_visible: next } : a));
            toast.success(next ? '홈페이지에 노출됩니다.' : '홈페이지에서 숨김 처리되었습니다.');
        } catch {
            toast.error('변경에 실패했습니다.');
        }
    };

    const formatDate = (d?: string) => {
        if (!d) return '-';
        const date = new Date(d);
        return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
    };

    const visibleCount = list.filter(a => a.is_visible).length;
    let filtered = list;
    if (filter === 'VISIBLE') filtered = list.filter(a => a.is_visible);
    if (filter === 'HIDDEN') filtered = list.filter(a => !a.is_visible);

    const columns: Column<Award>[] = [
        {
            key: 'is_visible',
            header: '노출',
            width: '70px',
            align: 'center',
            render: (value, row) => (
                <Switch
                    checked={!!value}
                    onClick={(e) => e.stopPropagation()}
                    onCheckedChange={() => handleToggle(row)}
                    aria-label="홈페이지 노출"
                />
            ),
        },
        { key: 'award_name', header: '상훈명', render: (v) => <span className="font-medium">{v}</span> },
        { key: 'issuing_organization', header: '수여기관', width: '200px', render: (v) => <span className="text-muted-foreground">{v}</span> },
        { key: 'award_date', header: '수여일', width: '120px', align: 'center', render: (v) => <span className="tabular-nums text-muted-foreground">{formatDate(v)}</span> },
        {
            key: 'photo_url',
            header: '사진',
            width: '70px',
            align: 'center',
            render: (v) => v ? (
                <img src={v} alt="상훈" className="w-9 h-9 rounded object-cover mx-auto border border-border" />
            ) : <span className="text-xs text-neutral-400">-</span>,
        },
    ];

    return (
        <div>
            <FilterCardGroup columns={3}>
                <FilterCard label="전체 상훈" count={loading ? undefined : list.length} suffix="건" active={filter === 'ALL'} onClick={() => setFilter('ALL')} />
                <FilterCard label="홈페이지 노출중" count={loading ? undefined : visibleCount} suffix="건" active={filter === 'VISIBLE'} onClick={() => setFilter('VISIBLE')} />
                <FilterCard label="비노출" count={loading ? undefined : list.length - visibleCount} suffix="건" active={filter === 'HIDDEN'} onClick={() => setFilter('HIDDEN')} />
            </FilterCardGroup>

            <Section
                className="overflow-hidden"
                title={
                    <>
                        상훈 목록
                        {!loading && (
                            <span className="text-xs font-normal text-muted-foreground tabular-nums">
                                총 <span className="font-medium text-foreground">{filtered.length}</span>건
                            </span>
                        )}
                    </>
                }
                description="홈페이지 회사소개 > 특허 및 상훈에 노출될 상훈을 큐레이션합니다. 상훈 자체의 정보 수정은 상훈 관리에서 가능합니다."
                flush
            >
                {loading ? (
                    <Loading text="상훈을 불러오는 중..." />
                ) : (
                    <Table
                        columns={columns}
                        data={filtered}
                        pagination
                        pageSize={15}
                        emptyMessage="해당하는 상훈이 없습니다."
                        onRowClick={(row) => navigate(`/admin/award?edit=${row.id}`)}
                    />
                )}
            </Section>
        </div>
    );
};

export default AwardVisibilityManager;
